import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ResponsiveLogo } from '../components/ThemeLogo';
import PublicHeader from '../components/PublicHeader';
import { toast } from 'sonner';
import {
  Mail, Building2, Clock, ArrowRight, Send, Loader2,
  ChevronDown, CheckCircle2, MessageSquare,
} from 'lucide-react';

const ICON_MAP = { mail: Mail, building: Building2, clock: Clock };

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Hero (two-column: image left, text right) ──
function ContactHero({ section }) {
  const [ref, vis] = useInView(0.1);
  return (
    <section ref={ref} className="py-12 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-odapto-orange/5 to-background" data-testid="contact-hero">
      <div className={`max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12 transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Left: Image */}
        <div className="w-full md:w-5/12 shrink-0">
          <img
            src="/landing/contact-banner.png"
            alt="Odapto Contact"
            className="w-full max-w-sm mx-auto md:max-w-none h-auto rounded-2xl object-contain"
            data-testid="contact-banner-image"
          />
        </div>
        {/* Right: Text */}
        <div className="flex-1 text-center md:text-left">
          {section.badge && (
            <span className="inline-block text-xs font-semibold tracking-wider uppercase text-odapto-orange bg-odapto-orange/10 px-3 py-1 rounded-full mb-4">
              {section.badge}
            </span>
          )}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight">{section.heading}</h1>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">{section.description}</p>
        </div>
      </div>
    </section>
  );
}

// ── Contact Form ──
function ContactForm({ section }) {
  const [ref, vis] = useInView();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), subject: subject.trim(), message: message.trim() }),
      });
      if (res.ok) {
        setSent(true);
        toast.success('Message sent! We\'ll get back to you soon.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send. Please try again.');
      }
    } catch {
      toast.error('Connection error. Please try again.');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <section className="py-16 px-4 sm:px-6" data-testid="contact-form-section">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3">Message Sent!</h2>
          <p className="text-muted-foreground mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
          <Button onClick={() => { setSent(false); setName(''); setEmail(''); setSubject(''); setMessage(''); }} variant="outline">
            Send Another Message
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="py-16 px-4 sm:px-6" data-testid="contact-form-section">
      <div className={`max-w-2xl mx-auto transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2">{section.heading}</h2>
          <p className="text-muted-foreground text-sm">{section.description}</p>
        </div>
        <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-name" className="text-sm font-medium mb-1.5 block">Name <span className="text-red-500">*</span></Label>
              <Input id="contact-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required data-testid="contact-name" />
            </div>
            <div>
              <Label htmlFor="contact-email" className="text-sm font-medium mb-1.5 block">Email <span className="text-red-500">*</span></Label>
              <Input id="contact-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="contact-email" />
            </div>
          </div>
          <div>
            <Label htmlFor="contact-subject" className="text-sm font-medium mb-1.5 block">Subject</Label>
            <Input id="contact-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="What is this about?" data-testid="contact-subject" />
          </div>
          <div>
            <Label htmlFor="contact-message" className="text-sm font-medium mb-1.5 block">Message <span className="text-red-500">*</span></Label>
            <textarea
              id="contact-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Tell us how we can help..."
              rows={5}
              required
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30"
              data-testid="contact-message"
            />
          </div>
          <Button type="submit" disabled={sending} className="w-full bg-odapto-orange hover:bg-odapto-orange-hover text-white h-11" data-testid="contact-submit">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
          </Button>
        </form>
      </div>
    </section>
  );
}

// ── Contact Info Cards ──
function ContactInfo({ section }) {
  const [ref, vis] = useInView();
  const cards = section.cards || [];
  return (
    <section ref={ref} className="py-16 px-4 sm:px-6 bg-muted/30" data-testid="contact-info">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-10">{section.heading}</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {cards.map((card, i) => {
            const Icon = ICON_MAP[card.icon] || Mail;
            return (
              <div
                key={i}
                className={`p-6 rounded-2xl bg-card border border-border text-center hover:shadow-lg transition-all duration-500 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-odapto-orange/10 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-odapto-orange" />
                </div>
                <h3 className="font-bold mb-1">{card.title}</h3>
                <p className="text-odapto-orange font-medium text-sm mb-1">{card.text}</p>
                <p className="text-muted-foreground text-xs">{card.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ──
function ContactFAQ({ section }) {
  const [ref, vis] = useInView();
  const [openIndex, setOpenIndex] = useState(null);
  const items = section.items || [];
  return (
    <section ref={ref} className="py-16 px-4 sm:px-6" data-testid="contact-faq">
      <div className={`max-w-2xl mx-auto transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <h2 className="text-2xl font-bold text-center mb-8">{section.heading}</h2>
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-sm hover:bg-muted/50 transition-colors"
                data-testid={`faq-q-${i}`}
              >
                {item.question}
                <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 ml-2 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA (reused from About page pattern) ──
function CTASection({ section }) {
  const [ref, vis] = useInView();
  return (
    <section ref={ref} className="relative py-20 px-4 sm:px-6 overflow-hidden" data-testid="contact-cta">
      <div className="absolute inset-0 bg-gradient-to-br from-odapto-orange/10 via-background to-odapto-teal/10" />
      <div className="absolute top-10 left-10 w-72 h-72 bg-odapto-orange/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-odapto-teal/5 rounded-full blur-3xl" />
      <div className={`relative max-w-3xl mx-auto text-center transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <h2 className="text-3xl sm:text-4xl font-bold mb-5">{section.heading}</h2>
        <p className="text-muted-foreground text-base sm:text-lg mb-8 max-w-xl mx-auto">{section.description}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {section.primary_cta && (
            <Link to={section.primary_cta.href || '/register'}>
              <Button size="lg" className="bg-odapto-orange hover:bg-odapto-orange-hover text-white px-8 h-12 text-base">
                {section.primary_cta.label} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
          {section.secondary_cta && (
            <Link to={section.secondary_cta.href || '/about-us'}>
              <Button size="lg" variant="outline" className="px-8 h-12 text-base">
                {section.secondary_cta.label}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

const SECTION_RENDERERS = {
  contact_hero: ContactHero,
  contact_form: ContactForm,
  contact_info: ContactInfo,
  contact_faq: ContactFAQ,
  cta: CTASection,
};

// ═══════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════

export default function ContactPage() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/pages/contact`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setPage)
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!page?.seo) return;
    document.title = page.seo.meta_title || 'Contact Odapto';
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', page.seo.meta_description);
    setMeta('og:title', page.seo.meta_title);
    setMeta('og:description', page.seo.meta_description);
  }, [page]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!page) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
        <Link to="/"><Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white">Go Home</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <PublicHeader />

      {(page.sections || []).map((section, i) => {
        const Renderer = SECTION_RENDERERS[section.type];
        if (!Renderer) return null;
        return <Renderer key={i} section={section} />;
      })}

      <footer className="bg-[#162F38] text-zinc-400 py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/"><ResponsiveLogo className="h-7 w-auto brightness-200" /></Link>
          <div className="flex items-center gap-6 text-xs">
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/about-us" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <span className="text-xs">&copy; 2026 Odapto. All rights reserved. | Website by <a href="https://depextechnologies.com/" className="text-zinc-300 hover:text-white transition-colors underline">DEPEX</a></span>
        </div>
      </footer>
    </div>
  );
}
